import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  CircularProgress,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { sessionService } from '../../services/sessionService';
import type { AgentContextSummaryDto } from '../../types/contextWindow';

export interface ContextSummaryListProps {
  sessionId: string;
}

/**
 * Displays stored context summaries for a session.
 */
export const ContextSummaryList = ({ sessionId }: ContextSummaryListProps) => {
  const [items, setItems] = useState<AgentContextSummaryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await sessionService.getContextSummaries(sessionId);
        if (!isMounted) return;
        setItems(data);
      } catch (err: any) {
        if (!isMounted) return;
        setError(err?.response?.data?.message || err?.message || 'Failed to load context summaries');
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, [sessionId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mt: 1 }}>
        {error}
      </Alert>
    );
  }

  if (!items.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        No summaries yet.
      </Typography>
    );
  }

  return (
    <Box>
      {items.map((s) => (
        <Accordion key={s.id} disableGutters>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2">
                Summary · {new Date(s.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {s.originalTokenCount.toLocaleString()} → {s.compressedTokenCount.toLocaleString()} tokens ·{' '}
                {s.messagesSummarized} msgs
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Range: {new Date(s.messageRangeStart).toLocaleString()} – {new Date(s.messageRangeEnd).toLocaleString()}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {s.summaryText}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

